/**
 * parseClipboardLines — Markdown-aware clipboard text parser.
 *
 * Splits clipboard text into structured lines for checklist item creation.
 * v1-compatible: same prefix rules used by formatObjectsForCopy in v1.
 *
 * Prefix rules:
 *   ## / # → Section
 *   - [ ]  → Task (Unfinished)
 *   - [x]  → Task (Canceled)
 *   - [~]  → Task (Completed)
 *   plain  → Task (Unfinished), with common list prefixes stripped
 *
 * Cap: 100 lines max.
 */

export interface ParsedLine {
  title: string
  type: "task" | "section"
  status?: "unfinished" | "completed" | "canceled"
}

export function parseClipboardLines(text: string): ParsedLine[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line): ParsedLine => {
      // Section: ## or #
      if (line.startsWith("## "))
        return { title: line.slice(3), type: "section" }
      if (line.startsWith("# "))
        return { title: line.slice(2), type: "section" }
      // Completed task (standard markdown checkbox)
      if (line.startsWith("- [x] "))
        return { title: line.slice(6), type: "task", status: "completed" }
      // Canceled task
      if (line.startsWith("- [~] "))
        return { title: line.slice(6), type: "task", status: "canceled" }
      // Unfinished task (explicit)
      if (line.startsWith("- [ ] "))
        return { title: line.slice(6), type: "task", status: "unfinished" }
      // Strip common list prefixes (-, *, •, 1.)
      const stripped = line.replace(/^(?:[-*•]\s*|\d+\.\s*)/i, "")
      return {
        title: stripped || line,
        type: "task",
        status: "unfinished",
      }
    })
    .filter((item) => item.title.length > 0)
    .slice(0, 100)
}
